/**
 * Building a colour family from a parent list and a colour list.
 *
 * A new product line is 8 parents × 32 colours = 256 mappings. Entering 256 rows is how mistakes
 * get made — a shifted column, a mistyped parent on row 173, and the wrong item code lands on a
 * goods received note. Entering 8 + 32 is not. So the admin gives the parents and the colours, and
 * the child codes are derived.
 *
 * **The derivation is measured, not invented.** Across all 198,180 existing mappings, replacing the
 * fabric marker in the parent with the colour code reproduces the real child code **99.93%** of the
 * time. Every one of the 148 exceptions belongs to a single odd parent, `HBNEO90B1`, which uses
 * `B1` where the rest of the catalogue uses `FB1`.
 *
 *     HBTF182FB1        + CTE002   →  HBTF182CTE002
 *     DVCT18219012FB1AB + J3310-3  →  DVCT18219012J3310-3AB     the AB suffix survives
 *     BCMJT182FB2       + H212-1   →  BCMJT182H212-1            the marker is not always FB1
 *     DVMD901908FB      + H51101-1 →  DVMD901908H51101-1        sometimes there is no digit
 *
 * That last 0.07% is exactly why a generated row is still validated like any other: a child code
 * Smartlink does not have is rejected whether a human typed it or this function produced it.
 * Deriving a code is a convenience, never a substitute for checking it exists.
 */

import { setPieceCodes } from './setPieceCodes.js';

const norm = (s: string): string => s.trim().toUpperCase();

/**
 * A colour CODE is an identifier and carries no whitespace.
 *
 * LSK's master settles it: 198,195 of 198,225 existing colour codes have no space. Typing
 * `CTE 001` once produced `DV15219010CTE 001A` — a code no tag will ever carry — across 12,362
 * mappings. The NAME keeps its spaces; it is prose, and `CTE 002 PEWTER` is how people read it.
 */
export const normColourCode = (s: string): string => s.replace(/\s+/g, '').toUpperCase();

/**
 * The parent's MATERIAL marker, replaced by the colour code.
 *
 * `FB` + a single digit is tried first and only the FIRST occurrence is replaced: `BFB122182FB1`
 * has a marker-shaped run at position 1, and a greedy or last-match rule mangles it. Trailing bare
 * forms are the fallback. Anything else returns null — the caller reports it rather than inventing
 * a code.
 *
 * **`PU` is a marker too**, and it was missing. LSK sell the same frame in fabric and in PU
 * (leatherette) with the marker in the same slot — `HBLTZ152FB1` / `HBLTZ152PU1` — and 1,778
 * active items carry a PU one. Without this, pasting a PU line into "Add a colour family" produced
 * zero mappings and told the admin their 179 valid item codes had "no recognisable fabric code".
 *
 * **The two markers derive the SAME child code**, because the colour replaces the marker entirely:
 * `HBLTZ152FB1` and `HBLTZ152PU1` both give `HBLTZ152<colour>`, and 1,613 of the 1,778 PU items
 * have an FB twin. That is not a flaw in the rule — it is LSK's own coding scheme, and it is
 * unambiguous in practice because the two colour vocabularies are disjoint: fabric colours are
 * lettered (`CTE002`, `J3310-3`), PU colours are the numeric `8xxx` range, and `DV15219088007`
 * already exists in the item master as exactly the code this produces. A derived child is still
 * validated against that master before it is saved, which is what catches a colour applied to the
 * wrong material.
 */
export function deriveChildSku(parentSku: string, colourCode: string): string | null {
  const parent = norm(parentSku);
  const code = normColourCode(colourCode);
  if (parent === '' || code === '') return null;
  if (/FB\d/.test(parent)) return parent.replace(/FB\d/, code);
  if (/PU\d/.test(parent)) return parent.replace(/PU\d/, code);
  if (/FB$/.test(parent)) return parent.replace(/FB$/, code);
  if (/PU$/.test(parent)) return parent.replace(/PU$/, code);
  return null;
}

export type GeneratedRow = {
  childSku: string;
  parentSku: string;
  colourCode: string;
  colourName: string;
};

export type Generated = {
  rows: GeneratedRow[];
  /** Parents with no recognisable marker. Reported, never silently dropped. */
  undecodable: string[];
};

export function generateColourFamily(
  parents: string[],
  colours: Array<{ code: string; name: string }>,
): Generated {
  const cleanParents = parents.map(norm).filter(Boolean);
  const cleanColours = colours
    .map((c) => ({ code: normColourCode(c.code), name: c.name.trim() }))
    .filter((c) => c.code !== '');

  const rows: GeneratedRow[] = [];
  const undecodable: string[] = [];

  for (const parentSku of cleanParents) {
    // Test the marker once per parent: a parent that cannot be decoded fails for every colour, and
    // reporting it 32 times would bury the one fact the admin needs.
    if (deriveChildSku(parentSku, 'X') === null) {
      undecodable.push(parentSku);
      continue;
    }
    for (const colour of cleanColours) {
      const childSku = deriveChildSku(parentSku, colour.code);
      if (childSku === null) continue;
      rows.push({ childSku, parentSku, colourCode: colour.code, colourName: colour.name });
    }
  }

  return { rows, undecodable };
}

/**
 * The NAME a new colour child should carry in Smartlink.
 *
 * LSK's product master already has a convention and this follows it rather than inventing one:
 *
 *   parent  BENCH MAJESTIC 182x190 (FABRIC)
 *   child   BENCH MAJESTIC 182x190 (CTE 001 OYSTER)
 *
 * The marker replaced is `(FABRIC)` **specifically, not the first bracket**. Measured across 12,000
 * real parent/child pairs in 04007: `(FABRIC)` matched **100.00%**, "first bracket" 98.63%, "last
 * bracket" 50.92%. The difference is names like `BENCH B122@STD 56"(W)(FABRIC)`, where the first
 * bracket is a width and replacing it would put the colour in the wrong slot while leaving the word
 * FABRIC on a product that is no longer plain fabric.
 *
 * Copying the parent's name verbatim — which is what this replaced — produced a product named
 * "(FABRIC)" in a named colour, wrong on every picker screen it appeared on.
 */
export function deriveChildName(
  parentName: string | null | undefined,
  colourName: string,
): string | null {
  if (parentName === null || parentName === undefined) return null;
  const colour = colourName.trim();
  if (colour === '') return parentName;
  // The WORD, not the bracket. Both score 100.00% on the 12,000 well-formed pairs, but names like
  // `BEDFRAME ELEVA 180X200 (FABRIC(A+B)@…` have a bracket that never closes — and replacing the
  // bracket there swallows `(A+B)`, destroying the set marker that `derivePieceName` needs next.
  if (/FABRIC/i.test(parentName)) return parentName.replace(/FABRIC/i, colour);
  // No fabric marker at all: the first bracket is the colour slot, verified on the same sample.
  if (/\([^)]*\)/.test(parentName)) return parentName.replace(/\([^)]*\)/, `(${colour})`);
  return `${parentName} (${colour})`;
}

/** A set piece: one physical box, mapped so quantities PAIR rather than add. */
export type GeneratedSetRow = {
  childSku: string;
  parentSku: string;
  piece: string;
  qtyPerSet: string;
  colourCode: string;
  colourName: string;
};

/**
 * Coloured SET PIECES — the third level of the hierarchy.
 *
 *     BFJOY152(12)FB1AB          the accounting item (what is sold, and posted)
 *     ├── BFJOY152(12)ELGBA      piece A, in colour ELGB   ← a tag goes here
 *     └── BFJOY152(12)ELGBB      piece B, in colour ELGB   ← and here
 *
 * These MUST be split mappings, not colour mappings. A colour mapping ADDS: five A-boxes and five
 * B-boxes would report ten units against a five-set order. A split mapping PAIRS — five complete
 * sets — and surfaces an unmatched box as an orphan instead of inventing stock.
 *
 * Why the codes need to differ per piece at all: if both boxes carried one `…AB` code, a scan of
 * two boxes reports `real_qty = 2` with no way to tell ONE COMPLETE SET from TWO A-BOXES. Pairing
 * needs two distinct codes to take a minimum across; a single code can only be halved, which turns
 * a half-shipment into a whole one.
 *
 * Only a `…AB` item has pieces. A piece code entered by mistake (`…FB1A`) is refused rather than
 * decoded — that mistake is what sent 296 rows through the colour path and produced nothing.
 */
export function generateSetFamily(
  parents: string[],
  colours: Array<{ code: string; name: string }>,
  /**
   * Codes already known to be split into pieces.
   *
   * A set code ending in `AB` announces itself. One that does not — `BFJOY152D124DW10FB1` — is
   * indistinguishable from a plain colour parent by shape alone, so the caller supplies the fact.
   * The screen has it already: it is what `decideFamilyKind` returns as `split`, and what drew the
   * "set item" badge in the first place.
   */
  opts?: {
    knownSets?: readonly string[];
    /**
     * The pieces each set was actually SPLIT into, where they are not A and B.
     *
     * A bed split into UPF/LPF/SD (LSK, 2026-10-03) must be coloured in UPF/LPF/SD. Given A/B, the
     * generator would create boxes that do not exist, and mapped beside the real pieces they would
     * make a set that can never complete — pairing requires every mapped piece.
     */
    piecesByParent?: Readonly<Record<string, readonly string[]>>;
  },
): { rows: GeneratedSetRow[]; undecodable: string[] } {
  const cleanParents = parents.map(norm).filter(Boolean);
  const cleanColours = colours
    .map((c) => ({ code: normColourCode(c.code), name: c.name.trim() }))
    .filter((c) => c.code !== '');

  const known = new Set((opts?.knownSets ?? []).map(norm).filter(Boolean));
  const rows: GeneratedSetRow[] = [];
  const undecodable: string[] = [];

  for (const parentSku of cleanParents) {
    // TWO shapes of set code exist in LSK's master, and requiring the first excluded the second:
    //
    //   DVCT18219010FB1AB     ends in AB — strip it, then append the piece
    //   BFJOY152D124DW10FB1   does not, yet IS a set: its description reads `…(A+B)@FULLY COVER`
    //                         and its pieces are already mapped as `…FB1A` / `…FB1B`
    //
    // The code alone cannot tell the second apart from `HBATR182FB1`, a plain colour parent — they
    // are the same shape. So the CALLER says which codes are split; the screen already knows,
    // because it is the same fact that drew the "set item" badge.
    //
    // Without this, nobody could add colours to such an item at all, and the only apparent escape
    // was to call it a non-set — which would break the pairing: 5 A-pieces and 3 B-pieces is THREE
    // bedframes, not eight of anything.
    const isSet = /AB$/.test(parentSku) || known.has(parentSku);
    const colourSet = isSet ? deriveChildSku(parentSku, 'X') : null;
    if (colourSet === null) {
      undecodable.push(parentSku);
      continue;
    }
    for (const colour of cleanColours) {
      const withColour = deriveChildSku(parentSku, colour.code);
      if (withColour === null) continue;
      // `…<colour>AB` → the two boxes are `…<colour>A` and `…<colour>B`, exactly as the existing
      // `FB1AB` → `FB1A`/`FB1B` pair is named. Named pieces go on behind a hyphen, stripping
      // nothing: `…<colour>2P-UPF`. One rule for both, the one the base split uses.
      const pieces = opts?.piecesByParent?.[parentSku] ?? ['A', 'B'];
      for (const r of setPieceCodes(withColour, pieces.join('/'))) {
        rows.push({
          childSku: r.childSku,
          parentSku,
          piece: r.piece,
          qtyPerSet: '1',
          colourCode: colour.code,
          colourName: colour.name,
        });
      }
    }
  }

  return { rows, undecodable };
}

/**
 * The NAME a set piece carries, composed on top of the colour substitution.
 *
 *   accounting item  BEDFRAME JOY 152 (FABRIC)(DIVAN 12")(A+B)@FULLY COVER
 *   in a colour      BEDFRAME JOY 152 (3446-10)(DIVAN 12")(A+B)@FULLY COVER   ← deriveChildName
 *   piece A of it    BEDFRAME JOY 152 (3446-10)(DIVAN 12")[PIECE A]@FULLY COVER
 *
 * `(A+B)` → `[PIECE x]` is the master's own convention, read off the existing `FB1A`/`FB1B` rows.
 * The two substitutions are independent, which is what lets them compose on the malformed names —
 * `BFELEVA180FB1AB` is `…(FABRIC(A+B)@…`, with a bracket that never closes.
 */
export function derivePieceName(name: string | null | undefined, piece: string): string | null {
  if (name === null || name === undefined) return null;
  if (/\(A\+B\)/i.test(name)) return name.replace(/\(A\+B\)/i, `[PIECE ${piece}]`);
  return `${name} [PIECE ${piece}]`;
}
